import { type ReactNode, useCallback, useRef, useState } from "react";

export const PANEL_TOAST_DURATION_MS = 5000;
/** Failures stay up longer, so there is time to read them and use their action. */
export const PANEL_TOAST_ERROR_DURATION_MS = 10_000;

export type PanelToastTone = "success" | "danger";

export type PanelToastAction = { label: string; onClick: () => void };

export type PanelToastOptions = {
	/** `danger` reports a failure: red icon, alert role, and a longer timer. @default "success" */
	tone?: PanelToastTone;
	/** One follow-up, e.g. reopening the form that failed to save. */
	action?: PanelToastAction;
};

export type PanelToastData = { id: number; message: ReactNode } & PanelToastOptions;

/**
 * State for one `PanelToast`. A panel shows a single toast, so `showToast` replaces whatever is
 * up. `dismissToast(id)` only clears the toast it was issued for, so a late timer from a replaced
 * toast cannot dismiss its successor. Call it with no id to clear unconditionally.
 */
export const usePanelToast = () => {
	const [toast, setToast] = useState<PanelToastData | null>(null);
	const nextId = useRef(0);

	const showToast = useCallback((message: ReactNode, options?: PanelToastOptions) => {
		nextId.current += 1;
		setToast({ id: nextId.current, message, ...options });
	}, []);

	const dismissToast = useCallback((id?: number) => {
		setToast((current) => (id === undefined || current?.id === id ? null : current));
	}, []);

	return { toast, showToast, dismissToast };
};
